import * as React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../alert-dialog";
import { Input } from "../input";
import { Label } from "../label";
import { buttonVariants } from "../button";
import { cn } from "../../../lib/utils";

export interface ConfirmOptions {
  title: string;
  description?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
}

export interface PromptOptions {
  title: string;
  description?: React.ReactNode;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  required?: boolean;
}

type ConfirmRequest = {
  kind: "confirm";
  options: ConfirmOptions;
  resolve: (ok: boolean) => void;
};

type PromptRequest = {
  kind: "prompt";
  options: PromptOptions;
  resolve: (value: string | null) => void;
};

type Request = ConfirmRequest | PromptRequest;

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  prompt: (options: PromptOptions) => Promise<string | null>;
}

const ConfirmContext = React.createContext<ConfirmContextValue | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [request, setRequest] = React.useState<Request | null>(null);
  const [promptValue, setPromptValue] = React.useState("");

  const confirm = React.useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setRequest({ kind: "confirm", options, resolve });
      }),
    [],
  );

  const prompt = React.useCallback(
    (options: PromptOptions) =>
      new Promise<string | null>((resolve) => {
        setPromptValue(options.defaultValue ?? "");
        setRequest({ kind: "prompt", options, resolve });
      }),
    [],
  );

  const handleCancel = () => {
    if (!request) return;
    if (request.kind === "confirm") request.resolve(false);
    else request.resolve(null);
    setRequest(null);
  };

  const handleConfirm = () => {
    if (!request) return;
    if (request.kind === "confirm") {
      request.resolve(true);
    } else {
      const trimmed = promptValue.trim();
      if (request.options.required && !trimmed) return;
      request.resolve(promptValue);
    }
    setRequest(null);
  };

  const open = request !== null;
  const options = request?.options;
  const isPrompt = request?.kind === "prompt";
  const destructive =
    request?.kind === "confirm" && request.options.destructive === true;

  const value = React.useMemo<ConfirmContextValue>(
    () => ({ confirm, prompt }),
    [confirm, prompt],
  );

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <AlertDialog
        open={open}
        onOpenChange={(next) => {
          if (!next) handleCancel();
        }}
      >
        <AlertDialogContent>
          {options && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>{options.title}</AlertDialogTitle>
                {options.description && (
                  <AlertDialogDescription>
                    {options.description}
                  </AlertDialogDescription>
                )}
              </AlertDialogHeader>
              {isPrompt && (
                <div className="space-y-2">
                  {(request as PromptRequest).options.label && (
                    <Label htmlFor="confirm-prompt-input">
                      {(request as PromptRequest).options.label}
                    </Label>
                  )}
                  <Input
                    id="confirm-prompt-input"
                    autoFocus
                    value={promptValue}
                    onChange={(e) => setPromptValue(e.target.value)}
                    placeholder={(request as PromptRequest).options.placeholder}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleConfirm();
                      }
                    }}
                  />
                </div>
              )}
              <AlertDialogFooter>
                <AlertDialogCancel onClick={handleCancel}>
                  {options.cancelText ?? "Cancel"}
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleConfirm}
                  className={cn(
                    destructive &&
                      buttonVariants({ variant: "destructive" }),
                  )}
                >
                  {options.confirmText ??
                    (destructive ? "Delete" : isPrompt ? "Submit" : "Confirm")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirmContext(): ConfirmContextValue {
  const ctx = React.useContext(ConfirmContext);
  if (!ctx) {
    throw new Error(
      "useConfirm/usePrompt must be used inside <ConfirmProvider />. Mount it near the root of your app.",
    );
  }
  return ctx;
}

export function useConfirm() {
  return useConfirmContext().confirm;
}

export function usePrompt() {
  return useConfirmContext().prompt;
}
